import { makeDefaultWall, type WallInput } from "@tasduvar/engine";
import { autoSizeInputKey, type AutoSizeState } from "../../state/useAutoSize";

/**
 * Akıllı proje adım çubuğu modeli (saf; Cem 2026-10-06 "programımız yönlendirsin doğru yola"):
 * ① Zemin ve yükler → ② Boyutlandır → ③ Seç ve uygula → ④ Rapor / Çizim.
 */
export type StepId = "soil" | "size" | "pick" | "out";
export interface GuideInput {
  /** ① sihirbaz uygulandı ya da duvarın zemin değerleri varsayılandan farklı */ soil: boolean;
  /** ② bu duvar için güncel (girdileri değişmemiş) tarama sonucu var */ scan: boolean;
  /** analiz sonucu: true sağlıyor · false sağlamıyor · null hesap yapılamadı */ ok: boolean | null;
  /** duvar kesiti varsayılandan farklı (aday uygulandı ya da elle düzenlendi) */ edited: boolean;
  /** ④ rapor ya da Çizim paftası bu projede açıldı */ report: boolean;
}
export interface GuideStep {
  id: StepId; n: 1 | 2 | 3 | 4; done: boolean;
  /** sıradaki (ilk tamamlanmamış) adım — vurgulu, "➡️" */ next: boolean;
  /** ③: kontrol sağlanmıyor (kırmızı nokta) */ bad: boolean;
}

export function guideSteps(i: GuideInput): GuideStep[] {
  const done: Record<StepId, boolean> = { soil: i.soil, size: i.scan, pick: i.ok === true && (i.scan || i.edited), out: i.report };
  const ids: StepId[] = ["soil", "size", "pick", "out"];
  const next = ids.find((id) => !done[id]) ?? null;
  return ids.map((id, k) => ({ id, n: (k + 1) as GuideStep["n"], done: done[id], next: id === next, bad: id === "pick" && i.ok !== true }));
}

const DW = makeDefaultWall();
/** Zemin girdileri kullanıcı/rapor tarafından girilmiş mi (varsayılan dolgu 18/30/0, temel 19/30/0, σem 250 dışı) */
export function soilEntered(w: WallInput): boolean {
  const b = w.backfill.layers[0], d = DW.backfill.layers[0]!, f = w.foundationSoil, g = DW.foundationSoil;
  if (!b) return false;
  return b.gamma !== d.gamma || b.phi !== d.phi || b.c !== d.c || f.gamma !== g.gamma || f.phi !== g.phi || f.c !== g.c || f.drained !== g.drained
    || f.bearing.mode !== g.bearing.mode || f.bearing.sigmaAllow !== g.bearing.sigmaAllow;
}

/** Kesit, duvar tipinin varsayılan kesitinden farklı mı (çokgen kesit her zaman düzenlenmiş sayılır) */
export function geometryEdited(w: WallInput): boolean {
  const g = w.geometry;
  if (g.mode === "polygon") return true;
  const type = g.mode === "parametric" ? "masonry-gravity" : g.counterforts ? "rc-counterfort" : "rc-cantilever";
  return JSON.stringify(g) !== JSON.stringify(makeDefaultWall("", type).geometry);
}

/** ② tamam: tarama bu duvar için bitti ve sonucun girdileri duvarın şimdiki girdileriyle aynı */
export function scanFresh(a: Pick<AutoSizeState, "status" | "report" | "wallId" | "inputKey">, w: WallInput, height: number): boolean {
  return a.status === "done" && !!a.report && a.wallId === w.id && a.inputKey === autoSizeInputKey(w, height);
}
